import React, { useState, useEffect } from 'react';
import LoginPage from './auth/loginPage';
import ClusterHealth from './ui/rapid-healthcheck/clusterHealth';
import ModuleManagement from './ui/rapid-module-management/moduleManagement';
import IntakeFormModule from './ui/rapid-impementation-workflow/rapid-implementation-form'; // New modular native conversion
import './App.css';

export const RapidLogoSVG = ({ className }) => (
  <svg id='Layer_1' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1003.22 351.3' className={className}>
    <defs>
      <style>
        {".cls-1 { fill: #ce0e2d; } .cls-accent { fill: #e2e8f0; }"}
      </style>
    </defs>
    <path className='cls-1' d='M209.62,251.64l-29.31-42.34h-32.35v42.34h-35.17V99.66h65.79c40.6,0,66.01,21.06,66.01,55.15,0,22.8-11.51,39.52-31.27,47.99l34.09,48.85h-37.78ZM176.61,128.32h-28.66v52.98h28.66c21.5,0,32.35-9.99,32.35-26.49s-10.85-26.49-32.35-26.49'/>
    <path className='cls-1' d='M337.26,134.18l24.1,58.19h-48.2l24.1-58.19ZM320.11,99.65l-67.74,151.99h36.04l13.46-32.57h70.57l13.46,32.57h36.91l-67.96-151.99h-34.74Z'/>
    <path className='cls-1' d='M562.31,154.8c0,33.87-25.4,54.93-66.01,54.93h-30.61v41.9h-35.17V99.66h65.78c40.6,0,66.01,21.06,66.01,55.15M526.7,154.8c0-16.72-10.86-26.49-32.35-26.49h-28.66v52.76h28.66c21.5,0,32.35-9.77,32.35-26.27'/>
    <rect className='cls-1' x='575.04' y='99.66' width='35.17' height='151.98'/>
    <path className='cls-1' d='M694.94,99.66h-69.04v151.98h69.04c14.69,0,27.96-2.67,39.45-7.55l41.02-92.03c-9.7-32.41-39.86-52.41-80.47-52.41M693.2,222.76h-32.13v-94.23h32.13c30.18,0,49.94,18.02,49.94,47.12s-19.76,47.11-49.94,47.11'/>
    <path className='cls-accent' d='M801.11,174.79l15.77,38.07h-31.53l15.77-38.07ZM789.89,152.21l-15.28,34.29-26.84,60.21-2.2,4.94h23.58l8.81-21.31h46.16l8.81,21.31h24.15l-44.46-99.43h-22.73Z'/>
    <rect className='cls-accent' x='867.43' y='152.21' width='23.01' height='99.43'/>
  </svg>
);

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState({ name: '', role: '' });
  const [currentView, setCurrentView] = useState("landing");
  const [isInitializing, setIsInitializing] = useState(true);

  const DICOM_EXTERNAL_URL = process.env.REACT_APP_DICOM_UPLOAD_URL;

  // AE sign-off deep links (/?id=...&t=...) must land on the intake module, not
  // the landing page — the emailed link is the AE's only way in.
  const isSignoffDeeplink = () => {
    const params = new URLSearchParams(window.location.search);
    return Boolean(params.get('id') && params.get('t'));
  };

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const name = localStorage.getItem('auth_user');
    const role = localStorage.getItem('auth_userrole');

    if (token && name) {
      setUser({ name, role: role || '' });
      setIsLoggedIn(true);
      if (isSignoffDeeplink()) setCurrentView('im-intake-form');
    }
    setIsInitializing(false);
  }, []);

  const handleLogin = (userData) => {
    if (userData) {
      setUser(userData);
      setIsLoggedIn(true);
      if (isSignoffDeeplink()) setCurrentView('im-intake-form');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_userrole');
    setUser({ name: '', role: '' });
    setIsLoggedIn(false);
    setCurrentView('landing');
  };

  if (isInitializing) {
    return (
      <div className="login-screen">
        <div className="placeholder-view">
          <h2 style={{ color: '#e2e8f0' }}>LOADING SERVICES PORTAL...</h2>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) return <LoginPage onLogin={handleLogin} />;

  return (
    <div className="app-layout">
      <nav className="sidebar">
        <div className="sidebar-header clickable-header" onClick={() => setCurrentView('landing')}>
          <div className="logo-container">
            <RapidLogoSVG className="rapid-logo" />
            <span className="services-text">SERVICES</span>
          </div>
        </div>
        
        <ul className="sidebar-links">
          <li className={currentView === 'clusterHealth' ? 'active-link' : ''} onClick={() => setCurrentView('clusterHealth')}>
            Cluster Report
          </li>
          <li className={currentView === 'moduleMgmt' ? 'active-link' : ''} onClick={() => setCurrentView('moduleMgmt')}>
            Module Management
          </li>
          <li className={currentView === 'im-intake-form' ? 'active-link' : ''} onClick={() => setCurrentView('im-intake-form')}>
            IM-Intake-Form
          </li>
          <li className={currentView === 'dicom' ? 'active-link' : ''} onClick={() => setCurrentView('dicom')}>
            Dicom Study Upload
          </li>
          <li className={currentView === 'systemHealth' ? 'active-link' : ''} onClick={() => setCurrentView('systemHealth')}>
            System Health
          </li>
          <li className={currentView === 'tools' ? 'active-link' : ''} onClick={() => setCurrentView('tools')}>
            Tools
          </li>
        </ul>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">{user.name ? user.name.charAt(0).toUpperCase() : '?'}</div>
            <div className="user-info">
              <span className="user-name" style={{ textTransform: 'capitalize' }}>
                {user.name}
              </span>
              <span className="user-role">{user.role}</span>
            </div>
          </div>
          <div className="logout-link-container" onClick={handleLogout}>
            Logout <span>→</span>
          </div>
        </div>
      </nav>

      <main className="main-viewport">
        {currentView === 'landing' && (
          <div className="content-padding">
            <div className="placeholder-view">
              <h1>Welcome to Rapid Services Portal</h1>
              <p>Select a service from the sidebar to manage clinical operations.</p>
            </div>
          </div>
        )}
        
        {currentView === 'clusterHealth' && (
          <div className="content-padding">
            <ClusterHealth />
          </div>
        )}

        {currentView === 'dicom' && (
          <iframe src={DICOM_EXTERNAL_URL} title="Dicom" className="external-iframe-full" allowFullScreen />
        )}

        {/* NATIVE INTEGRATION: Bypassed standard structural .content-padding wrappers.
          This ensures the intake module's sticky headers, widescreen tables, 
          and workflow tracking banners span elegantly across the panel viewport.
        */}
        {currentView === 'im-intake-form' && (
        <div style={{ width: '100%', height: '100%', pointerEvents: 'auto' }}>
            <IntakeFormModule />
        </div>
        )}

        {currentView === 'moduleMgmt' && (
          <ModuleManagement />
        )}

        {['systemHealth'].includes(currentView) && (
          <div className="content-padding">
            <div className="placeholder-view">
              <h1>System Health</h1>
              <p>This module is under construction.</p>
            </div>
          </div>
        )}
        {['tools'].includes(currentView) && (
          <div className="content-padding">
            <div className="placeholder-view">
              <h1>{currentView.toUpperCase()}</h1>
              <p>This module is under construction.</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;